<template>
  <div class="god_page_container">
    <div class="page-title-row">
      <div>
        <h2>Dashboard de Métricas</h2>
        <p class="subtitle-text">Vista general de compras, ingresos y distribución geográfica</p>
      </div>
    </div>

    <!-- Date Range Filter Bar -->
    <div class="filter-card">
      <div class="date-filter-group">
        <div class="date-input-wrap">
          <label>Fecha inicio</label>
          <input
            type="date"
            required
            class="date-input"
            v-model="min_date" />
        </div>
        <div class="date-input-wrap">
          <label>Fecha fin</label>
          <input
            type="date"
            required
            class="date-input"
            v-model="max_date" />
        </div>
      </div>
      <button @click="getStatistics()" class="main_button-green update-btn">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"></polyline>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
        </svg>
        Actualizar gráficas
      </button>
    </div>

    <!-- Executive KPI Metric Cards -->
    <div class="kpi-grid">
      <div class="stat-card">
        <div class="kpi-header">
          <span class="stat-card-label">Total de compras</span>
          <div class="kpi-icon-wrap icon-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </div>
        </div>
        <div class="stat-card-value">{{ totalNumPurchases.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}</div>
      </div>

      <div class="stat-card">
        <div class="kpi-header">
          <span class="stat-card-label">Importe total</span>
          <div class="kpi-icon-wrap icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
        </div>
        <div class="stat-card-value">{{ totalAmountPaid.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€</div>
        <p class="stat-card-label-bottom">Planes app/web: {{ totalAmountPaidByPublicPlans.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€<br>Planes privados: {{ totalAmountPaidByPrivatePlans.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€</p>
      </div>

      <div class="stat-card">
        <div class="kpi-header">
          <span class="stat-card-label">Total GGE</span>
          <div class="kpi-icon-wrap icon-gray">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
            </svg>
          </div>
        </div>
        <div class="stat-card-value">{{ totalGGE.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€</div>
        <p class="stat-card-label-bottom">Planes app/web: {{ totalGGEByPublicPlans.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€<br>Planes privados: {{ totalGGEByPrivatePlans.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€</p>
      </div>

      <div class="stat-card">
        <div class="kpi-header">
          <span class="stat-card-label">Total GGU</span>
          <div class="kpi-icon-wrap icon-dark">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
          </div>
        </div>
        <div class="stat-card-value">{{ totalGGU.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€</div>
      </div>
    </div>

    <!-- Main Chart Section -->
    <div class="chart-card mt-lg">
      <div class="chart-card-header">
        <h3>Evolución de Compras e Ingresos</h3>
      </div>
      <div class="chart-wrapper">
        <canvas ref="chartRef" class="chart-canvas"></canvas>
      </div>
    </div>

    <!-- Secondary Charts Grid -->
    <div class="secondary-charts-grid mt-lg">
      <div class="chart-card">
        <div class="chart-card-header">
          <h3>Planes por Ciudad</h3>
        </div>
        <div class="chart-wrapper">
          <canvas ref="chartCitiesRef" class="chart-canvas-100"></canvas>
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-card-header flex justify-between items-center">
          <h3>Planes por Empresa</h3>
          <select 
            v-model="plansByCityFilter"
            v-if="plans_by_company"
            class="city-select">
            <option
              v-for="(item, index) in uniqueCities"
              :key="index"
              :value="item.company_city">
              {{ item.company_city }}
            </option>
          </select>
        </div>
        <div class="chart-wrapper">
          <canvas ref="chartPlansByCompanyRef" class="chart-canvas-tall"></canvas>
        </div>
      </div>

      <div class="chart-card branding-card">
        <img src="@/assets/images/home_logo.png" alt="Click and Plan God Logo" class="god-center-logo"/>
        <p class="branding-caption">Click and Plan God Management Portal</p>
      </div>
    </div>

    <ErrorPopup :errorMessage="error" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed, nextTick } from 'vue';
import { Chart, registerables } from 'chart.js';
import ErrorPopup from "@/components/common/ErrorPopup.vue";
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";
import { useRouter } from 'vue-router';

const error = ref(null);
const loadingStore = useLoadingStore();
const router = useRouter();

Chart.register(...registerables);

const purchases_by_date = ref([]);
const private_plans_by_date = ref([]);
const plans_by_city = ref([]);
const plans_by_company = ref([]);
const formatDate = (date) => date.toISOString().split('T')[0];
const plansByCityFilter = ref('');

const today = new Date();
const lastMonth = new Date();
lastMonth.setDate(today.getDate() - 30);

const max_date = ref(formatDate(today));
const min_date = ref(formatDate(lastMonth));

const chartLabels = computed(() => purchases_by_date.value.map(item => item.day));
const comprasData = computed(() => purchases_by_date.value.map(item => item.numero_compras));
const totalPagadoData = computed(() => purchases_by_date.value.map(item => parseFloat(item.suma_total_pagado) || 0));
const sumaGGEData = computed(() => purchases_by_date.value.map(item => parseFloat(item.suma_GGE) || 0));
const sumaGGEPrivatePlansData = computed(() => private_plans_by_date.value.map(item => parseFloat(item.total_gge) || 0));
const sumaGGUData = computed(() => purchases_by_date.value.map(item => parseFloat(item.suma_GGU) || 0));
const totalPagadoPrivatePlansData = computed(() => private_plans_by_date.value.map(item => parseFloat(item.total_importe) || 0));

const chartCitieslabels = computed(() => plans_by_city.value.map(item => item.city || "Sin ciudad"));
const chartCitesTotals = computed(() => plans_by_city.value.map(item => item.total));

const filteredPlans = computed(() => {
  if (!plansByCityFilter.value) return plans_by_company.value;
  return plans_by_company.value.filter(
    item => item.company_city === plansByCityFilter.value
  );
});

const chartPlansByCompanyLabels = computed(() =>
  filteredPlans.value.map(item => item.company || "Sin empresa")
);

const chartPlansByCompanyTotals = computed(() =>
  filteredPlans.value.map(item => item.num_plans)
);

const uniqueCities = computed(() => {
  const seen = new Set()
  return plans_by_company.value.filter(item => {
    if (!item.company_city || seen.has(item.company_city)) {
      return false
    }
    if (plansByCityFilter.value == '') {
      plansByCityFilter.value = item.company_city;
    }
    seen.add(item.company_city)
    return true
  })
})

const chartRef = ref(null);
const chartCitiesRef = ref(null);
const chartPlansByCompanyRef = ref(null);
const totalNumPurchases = ref(0.0);
const totalAmountPaidByPublicPlans = ref(0.0);
const totalAmountPaidByPrivatePlans = ref(0.0);
const totalAmountPaid = ref(0.0);
const totalGGEByPublicPlans = ref(0.0);
const totalGGEByPrivatePlans = ref(0.0);
const totalGGE = ref(0.0);
const totalGGU = ref(0.0);
let chartInstance = null;
let chartCitiesInstance = null;
let chartPlansByCompanyInstance = null;

onMounted(() => {
  chartInstance = new Chart(chartRef.value, {
    type: 'bar',
    data: {
      labels: chartLabels.value,
      datasets: [
        { type: 'line', label: 'Número de compras', data: comprasData.value, stack: 'compras', borderWidth: 2, tension: 0.2, borderColor: '#FF0000', backgroundColor: '#FF000077'},
        { type: 'bar', label: 'Total pagado', data: totalPagadoData.value, stack: 'grupoPagos', borderWidth: 2, tension: 0.2, borderColor: '#20CDA8', backgroundColor: '#20CDA877'},
        { type: 'bar', label: 'Total pagado Talleres Privados', data: totalPagadoPrivatePlansData.value, stack: 'grupoPagos', borderWidth: 2, tension: 0.2, borderColor: '#107853', backgroundColor: '#10785377'},
        { type: 'line', label: 'GGE', data: sumaGGEData.value, stack: 'GGE', borderWidth: 2, tension: 0.2, borderColor: '#003F7D', backgroundColor: '#003F7D77'},
        { type: 'line', label: 'GGE Talleres Privados', data: sumaGGEPrivatePlansData.value, stack: 'GGE', borderWidth: 2, tension: 0.2, borderColor: '#000A48', backgroundColor: '#000A4877'},
        { type: 'line', label: 'GGU', data: sumaGGUData.value, stack: 'GGU', borderWidth: 2, tension: 0.2, borderColor: '#FF8C42', backgroundColor: '#FF8C4277'}
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        x: {
          stacked: true 
        },
        y: {
          beginAtZero: true,
          stacked: true
        }
      }
    }
  });

  chartCitiesInstance = new Chart(chartCitiesRef.value, {
    type: 'bar',
    data: {
      labels: chartCitieslabels.value,
      datasets: [
        {
          label: 'Número de planes por ciudad',
          data: chartCitesTotals.value,
          borderWidth: 2,
          borderColor: '#20CDA8',
          backgroundColor: '#20CDA877'
        }
      ]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          beginAtZero: true
        },
        y: {
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 30,
            callback: function(value) {
              const label = this.getLabelForValue(value)
              const maxLength = 20
              return label.length > maxLength
                ? label.substring(0, maxLength) + '…'
                : label
            }
          }
        }
      }
    }
  });

  chartPlansByCompanyInstance = new Chart(chartPlansByCompanyRef.value, {
    type: 'bar',
    data: {
      labels: chartPlansByCompanyLabels.value,
      datasets: [
        {
          label: 'Número de planes por empresa',
          data: chartPlansByCompanyTotals.value,
          borderWidth: 2,
          borderColor: '#20CDA8',
          backgroundColor: '#20CDA877'
        }
      ]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          beginAtZero: true
        },
        y: {
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 30,
            callback: function(value) {
              const label = this.getLabelForValue(value)
              const maxLength = 20
              return label.length > maxLength
                ? label.substring(0, maxLength) + '…'
                : label
            }
          }
        }
      }
    }
  });
});

watch(purchases_by_date, () => {
  if (chartInstance) {
    chartInstance.data.labels = chartLabels.value;
    chartInstance.data.datasets[0].data = comprasData.value;
    chartInstance.data.datasets[1].data = totalPagadoData.value;
    chartInstance.data.datasets[2].data = totalPagadoPrivatePlansData.value;
    chartInstance.data.datasets[3].data = sumaGGEData.value;
    chartInstance.data.datasets[4].data = sumaGGEPrivatePlansData.value;
    chartInstance.data.datasets[5].data = sumaGGUData.value;
    chartInstance.update();
  }

  totalNumPurchases.value = purchases_by_date.value.reduce((sum, item) => sum + item.numero_compras, 0);
  totalAmountPaidByPublicPlans.value = purchases_by_date.value.reduce((sum, item) => sum + (parseFloat(item.suma_total_pagado) || 0), 0);
  totalAmountPaidByPrivatePlans.value = private_plans_by_date.value.reduce((sum, item) => sum + (parseFloat(item.total_importe) || 0), 0);
  totalAmountPaid.value = totalAmountPaidByPublicPlans.value + totalAmountPaidByPrivatePlans.value;
  totalGGEByPublicPlans.value = purchases_by_date.value.reduce((sum, item) => sum + (parseFloat(item.suma_GGE) || 0), 0);
  totalGGEByPrivatePlans.value = private_plans_by_date.value.reduce((sum, item) => sum + (parseFloat(item.total_gge) || 0), 0);
  totalGGE.value = totalGGEByPublicPlans.value + totalGGEByPrivatePlans.value;
  totalGGU.value = purchases_by_date.value.reduce((sum, item) => sum + (parseFloat(item.suma_GGU) || 0), 0);
});

watch(plans_by_city, () => {
  if (chartCitiesInstance) {
    chartCitiesInstance.data.labels = chartCitieslabels.value;
    chartCitiesInstance.data.datasets[0].data = chartCitesTotals.value;
    chartCitiesInstance.update();
  }
});

watch(chartPlansByCompanyTotals, () => {
  if (chartPlansByCompanyInstance) {
    chartPlansByCompanyInstance.data.labels = chartPlansByCompanyLabels.value;
    chartPlansByCompanyInstance.data.datasets[0].data = chartPlansByCompanyTotals.value;
    chartPlansByCompanyInstance.update();
  }
});

const getStatistics = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await callAPIToGetStatistics();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await callAPIToGetStatistics();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      showError("Se ha producido un error obteniendo las estadísticas.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      showError(data.message);
      private_plans_by_date.value = [];
      purchases_by_date.value = [];
      plans_by_city.value = [];
      plans_by_company.value = [];
    } else {
      private_plans_by_date.value = data.stats.private_plans_by_date;
      purchases_by_date.value = data.stats.purchases_by_date;
      plans_by_city.value = data.stats.plans_by_city;
      plans_by_company.value = data.stats.plans_by_company;
    }
  } catch (exception) {
    showError(exception.message);
  } finally {
    loadingStore.setLoading(false);
  }
}

async function callAPIToGetStatistics() {
  return await fetch(process.env.VUE_APP_API_URL+"api/god/stats/get?user_id="+localStorage.getItem('user_id')+"&min_date="+min_date.value+"&max_date="+max_date.value,
      {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
    });
}

const showError = async (message) => {
  error.value = null;
  await nextTick(); // Espera a que Vue reaccione al cambio
  error.value = message;
};

getStatistics();

</script>

<style scoped>
.subtitle-text {
  font-size: 0.875rem;
  color: var(--text-color-light);
  margin-top: -0.5rem;
}

/* Date filter bar */
.filter-card {
  background: #FFFFFF;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.date-filter-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.date-input-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.date-input-wrap label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-color-dark);
}

.date-input {
  min-width: 170px;
  height: 42px !important;
  font-size: 13.5px !important;
}

.update-btn {
  height: 42px;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
}

.kpi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kpi-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-primary {
  background-color: rgba(32, 205, 168, 0.14);
  color: var(--primary-color);
}

.icon-teal {
  background-color: rgba(32, 205, 168, 0.22);
  color: #0ea685;
}

.icon-gray {
  background-color: rgba(96, 125, 148, 0.15);
  color: var(--clear-gray);
}

.icon-dark {
  background-color: rgba(47, 72, 88, 0.12);
  color: var(--gray);
}

/* Chart Cards */
.chart-card {
  background: #FFFFFF;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  transition: box-shadow var(--transition-fast);
}

.chart-card:hover {
  box-shadow: var(--shadow-md);
}

.chart-card-header {
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color-subtle);
}

.chart-card-header h3 {
  font-size: 1.1rem;
  margin-bottom: 0;
  color: var(--text-color-dark);
}

.city-select {
  max-width: 200px;
  height: 38px !important;
  font-size: 13px !important;
  padding: 4px 12px !important;
}

.chart-wrapper {
  position: relative;
  width: 100%;
}

.chart-canvas {
  width: 100% !important;
  height: 480px !important;
  max-height: 480px;
}

.secondary-charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.25rem;
}

.chart-canvas-100 {
  width: 100% !important;
  height: 380px !important;
}

.chart-canvas-tall {
  width: 100% !important;
  height: 380px !important;
}

/* Branding Card */
.branding-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2.5rem 1.5rem;
  background: linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%);
}

.god-center-logo {
  max-width: 220px;
  height: auto;
  opacity: 0.9;
  transition: transform var(--transition-normal);
}

.god-center-logo:hover {
  transform: scale(1.04);
}

.branding-caption {
  margin-top: 1.25rem;
  font-size: 0.85rem;
  color: var(--text-color-light);
  font-weight: 500;
  margin-bottom: 0;
}

.stat-card-label-bottom {
  font-size: 0.825rem;
  font-weight: 500;
  color: var(--text-color-light);
}


@media (max-width: 768px) {
  .filter-card {
    flex-direction: column;
    align-items: stretch;
  }

  .date-filter-group {
    flex-direction: column;
  }

  .update-btn {
    width: 100%;
  }

  .secondary-charts-grid {
    grid-template-columns: 1fr;
  }
}
</style>
